import { useMemo } from "react";
import { pointInPolygon } from "../../lib/worldLayout";

const svgPoints = (points) => points.map(([x, z]) => `${x},${-z}`).join(" ");

function seededRandom(seed) {
  let h = 2166136261;
  for (const c of String(seed)) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function polygonArea(points) {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, z1] = points[i];
    const [x2, z2] = points[(i + 1) % points.length];
    area += x1 * z2 - x2 * z1;
  }
  return Math.abs(area / 2);
}

function useIslandShapes(outline, seed) {
  return useMemo(() => {
    if (!outline?.length) return null;
    const cx = outline.reduce((s, p) => s + p[0], 0) / outline.length;
    const cz = outline.reduce((s, p) => s + p[1], 0) / outline.length;
    const scaled = (f) => outline.map(([x, z]) => [cx + (x - cx) * f, cz + (z - cz) * f]);
    const grass = scaled(0.8);
    const random = seededRandom(seed);

    const trees = [];
    const want = Math.max(7, Math.min(44, Math.round(polygonArea(grass) / 420)));
    for (let attempt = 0; attempt < want * 25 && trees.length < want; attempt++) {
      const x = cx + (random() * 2 - 1) * 120;
      const z = cz + (random() * 2 - 1) * 120;
      if (!pointInPolygon([x, z], grass)) continue;
      trees.push({ x, z, r: 4.5 + random() * 3.5 });
    }

    const rocks = [];
    const every = Math.max(1, Math.round(outline.length / 7));
    outline.forEach(([x, z], i) => {
      if (i % every) return;
      const f = 1.07 + random() * 0.05;
      rocks.push({ x: cx + (x - cx) * f, z: cz + (z - cz) * f, r: 2.2 + random() * 2.2 });
    });

    return { center: [cx, cz], halo: scaled(1.12), sand: outline, grass, trees, rocks };
  }, [outline, seed]);
}

export default function IslandArt({ outline, seed, locked = false, selected = false, onClick }) {
  const shapes = useIslandShapes(outline, seed);
  if (!shapes) return null;
  const clickable = onClick ? { onClick, style: { cursor: "pointer" } } : {};
  return (
    <g>
      <polygon points={svgPoints(shapes.halo)} fill="rgba(120,205,225,0.18)" pointerEvents="none" />
      <polygon points={svgPoints(shapes.sand)} fill="rgba(3,12,28,0.45)" transform="translate(2,5)" pointerEvents="none" />
      <polygon points={svgPoints(shapes.sand)} fill="#E6CC8F"
        stroke={selected ? "#FFFFFF" : "#C9A867"} strokeWidth={selected ? 4 : 1.5} strokeLinejoin="round" {...clickable} />
      <polygon points={svgPoints(shapes.grass)} fill="url(#map_grass)" strokeLinejoin="round" {...clickable} />
      <g pointerEvents="none">
        {shapes.rocks.map((rock, i) => (
          <ellipse key={`r${i}`} cx={rock.x} cy={-rock.z} rx={rock.r * 1.3} ry={rock.r} fill="#8E979C" stroke="#6C7479" strokeWidth="0.6" />
        ))}
        {shapes.trees.map((tree, i) => (
          <g key={`t${i}`}>
            <circle cx={tree.x + 1} cy={-tree.z + 1.5} r={tree.r} fill="rgba(10,40,20,0.35)" />
            <circle cx={tree.x} cy={-tree.z} r={tree.r} fill="#2F6E30" />
            <circle cx={tree.x - tree.r * 0.3} cy={-tree.z - tree.r * 0.3} r={tree.r * 0.55} fill="#5FA548" />
          </g>
        ))}
      </g>
      {locked && <polygon points={svgPoints(shapes.halo)} fill="rgba(6,18,38,0.55)" pointerEvents="none" />}
    </g>
  );
}
